import {
  SettingsFixedPriorities,
  SettingsManager,
} from "@viamap/viamap2-common";
import {
  DEFAULT_ASSUMPTIONS,
  JPBSoilQualityFactors,
} from "./JPBState";

export const JB_7_9_SETTING_KEY = "jpb.assumptions.soilQualityFactors.JB_7_9";
export const JB_10_SETTING_KEY = "jpb.assumptions.soilQualityFactors.JB_10";
export const JB_7_10_SETTING_KEY = "jpb.assumptions.soilQualityFactors.JB_7_10";

export type LegacySoilQualityFactors = Partial<JPBSoilQualityFactors> & {
  JB_7_10?: number;
};

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

function readDottedOrNested(
  settings: Record<string, unknown>,
  dottedKey: string,
): unknown {
  if (Object.prototype.hasOwnProperty.call(settings, dottedKey)) {
    return settings[dottedKey];
  }
  const parts = dottedKey.split(".");
  let node: unknown = settings;
  for (const part of parts) {
    if (!node || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

function isCustomerOrUserPriority(priority: number | undefined): boolean {
  return (
    priority === SettingsFixedPriorities.CustomerSettings ||
    priority === SettingsFixedPriorities.UserSettings
  );
}

/**
 * If a persisted profile still has `JB_7_10` and not the split keys, copy that
 * value onto `JB_7_9` and `JB_10` so both start from the same user/customer default.
 */
export function migrateLegacyJb710SettingKeys(
  settings: Record<string, unknown>,
): Record<string, unknown> {
  const legacy = asFiniteNumber(readDottedOrNested(settings, JB_7_10_SETTING_KEY));
  if (legacy === undefined) return settings;

  const next = { ...settings };
  if (asFiniteNumber(readDottedOrNested(next, JB_7_9_SETTING_KEY)) === undefined) {
    next[JB_7_9_SETTING_KEY] = legacy;
  }
  if (asFiniteNumber(readDottedOrNested(next, JB_10_SETTING_KEY)) === undefined) {
    next[JB_10_SETTING_KEY] = legacy;
  }
  return next;
}

export function normalizeSoilQualityFactors(
  factors: LegacySoilQualityFactors | undefined | null,
  fallback: JPBSoilQualityFactors = DEFAULT_ASSUMPTIONS.soilQualityFactors,
): JPBSoilQualityFactors {
  return {
    JB_1_2: asFiniteNumber(factors?.JB_1_2) ?? fallback.JB_1_2,
    JB_3_4: asFiniteNumber(factors?.JB_3_4) ?? fallback.JB_3_4,
    JB_5_6: asFiniteNumber(factors?.JB_5_6) ?? fallback.JB_5_6,
    JB_7_9: asFiniteNumber(factors?.JB_7_9) ?? asFiniteNumber(factors?.JB_7_10) ?? fallback.JB_7_9,
    JB_10: asFiniteNumber(factors?.JB_10) ?? asFiniteNumber(factors?.JB_7_10) ?? fallback.JB_10,
    JB_11: asFiniteNumber(factors?.JB_11) ?? fallback.JB_11,
  };
}

export function readCustomerAndUserSoilQualityFactorOverrides(): LegacySoilQualityFactors {
  try {
    const overlay = SettingsManager.extractSettingsByPriority(
      SettingsFixedPriorities.CustomerSettings,
      SettingsFixedPriorities.UserSettings,
      true,
    ) as Record<string, unknown>;
    const migrated = migrateLegacyJb710SettingKeys(overlay);
    return {
      JB_7_9: asFiniteNumber(readDottedOrNested(migrated, JB_7_9_SETTING_KEY)),
      JB_10: asFiniteNumber(readDottedOrNested(migrated, JB_10_SETTING_KEY)),
      JB_7_10: asFiniteNumber(readDottedOrNested(overlay, JB_7_10_SETTING_KEY)),
    };
  } catch {
    return {};
  }
}

export function getJpbCompatibleSettingSourcePriority(key: string): number {
  const direct = SettingsManager.getSystemSettingSource(key);
  if (
    (key === JB_7_9_SETTING_KEY || key === JB_10_SETTING_KEY) &&
    !isCustomerOrUserPriority(direct?.priority)
  ) {
    const legacy = SettingsManager.getSystemSettingSource(JB_7_10_SETTING_KEY);
    if (legacy && isCustomerOrUserPriority(legacy.priority)) {
      return legacy.priority;
    }
  }
  return direct?.priority || 0;
}

export function createJpbCompatibleSettingsManager() {
  return {
    extractSettingsByPriority: (
      minPriority: number,
      maxPriority: number,
      flatten?: boolean,
    ) =>
      migrateLegacyJb710SettingKeys(
        SettingsManager.extractSettingsByPriority(
          minPriority,
          maxPriority,
          flatten,
        ) as Record<string, unknown>,
      ),
    clearByPriority: SettingsManager.clearByPriority.bind(SettingsManager),
    addSettings: SettingsManager.addSettings.bind(SettingsManager),
    getState: SettingsManager.getState.bind(SettingsManager),
    getSystemSetting: (key: string, defaultValue?: unknown, silentWarning?: boolean) => {
      if (key === JB_7_9_SETTING_KEY || key === JB_10_SETTING_KEY) {
        const overlay = SettingsManager.extractSettingsByPriority(
          SettingsFixedPriorities.CustomerSettings,
          SettingsFixedPriorities.UserSettings,
          true,
        ) as Record<string, unknown>;
        const migrated = migrateLegacyJb710SettingKeys(overlay);
        const overlayValue = asFiniteNumber(readDottedOrNested(migrated, key));
        if (overlayValue !== undefined) return overlayValue;
      }
      return SettingsManager.getSystemSetting(key, defaultValue, silentWarning);
    },
  };
}
